import { useState } from 'react';
import SelectRecherche from './SelectRecherche';
import { genererContrat, genererDocumentsContrat } from '../services/contratService';
import { toast } from './Toasts';
import '../assets/styles/Listes.css';

/**
 * « Générer le prochain document » : un choix (contrat, convocation, feuille de
 * présence…) et un bouton, posés sur chaque fiche d'une liste du parcours.
 *
 * Le choix est d'avance sur l'étape suivante du dossier (`parDefaut`) ; il se
 * change pour produire un autre document sans quitter la liste. Les documents
 * d'une journée (convocation, feuille, attestations, convocation et contrat du
 * formateur) se génèrent pour toutes les journées du contrat ; ce qui ne peut
 * pas l'être (pas de journée planifiée, pas de participant, pas de formateur)
 * est dit tel quel, par la réponse du serveur.
 *
 * Le rappel n'y figure pas : ce n'est pas un document mais un email qui part
 * tout seul avant la formation (page Rappel).
 */
const OPTIONS = [
  { value: 'contrat', label: 'Contrat' },
  { value: 'convocation', label: 'Convocation' },
  { value: 'feuille_presence', label: 'Feuille de présence' },
  { value: 'attestation', label: 'Attestations' },
  { value: 'convocation_formateur', label: 'Convocation formateur' },
  { value: 'contrat_formateur', label: 'Contrat formateur' },
];

function GenererSuivant({ devisId = null, contratId = null, parDefaut, onGenere, className = '' }) {
  // null = « celui qui s'impose » : il suit le dossier quand la liste se recharge.
  const [choix, setChoix] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const suivant = parDefaut || (contratId ? 'convocation' : 'contrat');

  /** Pourquoi un type ne peut pas être produit maintenant (ou null). */
  function indisponible(type) {
    if (type === 'contrat') {
      if (contratId) return 'Le contrat existe déjà (« Régénérer le PDF » dans sa fiche).';
      if (!devisId) return 'Il faut d’abord un devis.';
      return null;
    }
    return contratId ? null : 'Il faut d’abord un contrat.';
  }

  const effectif = choix && !indisponible(choix) ? choix : suivant;
  const libelle = OPTIONS.find((o) => o.value === effectif)?.label || effectif;
  const raison = indisponible(effectif);

  async function generer() {
    setEnCours(true);
    try {
      const res =
        effectif === 'contrat'
          ? await genererContrat(devisId)
          : await genererDocumentsContrat(contratId, effectif);
      toast.succes(res.message || `${libelle} généré.`);
      setChoix(null);
      onGenere?.(res);
    } catch (err) {
      toast.erreur(err.message || 'Génération impossible.');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className={`generer-suivant ${className}`.trim()} role="group" aria-label="Générer un document">
      <SelectRecherche
        className="generer-suivant-choix"
        value={effectif}
        onChange={(e) => setChoix(e.target.value)}
        disabled={enCours}
      >
        {OPTIONS.map((o) => (
          <option key={o.value} value={o.value} disabled={Boolean(indisponible(o.value))}>
            {o.label}
          </option>
        ))}
      </SelectRecherche>
      <button
        type="button"
        className="devis-btn devis-btn-sm devis-btn-primary generer-suivant-bouton"
        onClick={generer}
        disabled={enCours || Boolean(raison)}
        title={raison || `Générer : ${libelle}`}
      >
        {enCours ? 'En cours' : 'Générer'}
      </button>
    </div>
  );
}

export default GenererSuivant;
